// Jelly mountains levels. The id is fixed (progress is saved under it) – don't change it once the level is published.
import { defineLevels } from '../../core/registry';
import { on } from '../../core/level-helpers';
import type { StaticData } from '../../core/types';

const P = 16;
const biscuit = (x: number, y: number, w: number, h?: number, a?: number): StaticData => ({ x, y, w, h: h || 22, a: a || 0, skin: 'jelly_biscuit' });
const chocolate = (x: number, y: number, w: number, h?: number, a?: number): StaticData => ({ x, y, w, h: h || 40, a: a || 0, skin: 'jelly_chocolate' });

export const levels = defineLevels([
  // 1 – Wiggle – the jelly sends the gumball back up and over into the bowl (new: jelly block)
  (() => {
    const s1 = biscuit(160, 220, 280, 22, 10);
    return {
      id: 'wiggle', par: 1, tools: { jelly_block: 1 }, learn: ['spring', 'materials'], intro: 'jelly_block',
      statics: [s1, chocolate(330, 760, 660, 80), chocolate(700, 640, 30, 280), chocolate(950, 760, 500, 80)],
      balls: [on(s1, -100, P)],
      goal: { type: 'bowl', x: 900, y: 720, w: 220 },
      objs: [{ t: 'duck', x: 600, y: 300 }],
      solution: [{ t: 'jelly_block', x: 520, y: 620, a: 0 }],
    };
  })(),

  // 2 – Stop – the ball rushes too fast; the marshmallow stops it so it drops into the bowl (new: marshmallow)
  (() => {
    const s1 = biscuit(330, 330, 640, 22, 14);
    return {
      id: 'stop', par: 1, tools: { jelly_marshmallow: 1 }, learn: ['materials', 'friction'], intro: 'jelly_marshmallow',
      statics: [s1, chocolate(780, 760, 240, 80)],
      balls: [on(s1, -290, P)],
      goal: { type: 'bowl', x: 780, y: 720, w: 220 },
      objs: [{ t: 'duck', x: 1000, y: 300 }],
      solution: [{ t: 'jelly_marshmallow', x: 840, y: 460, a: 0 }],
    };
  })(),

  // 3 – Clack – hard candy clacks: a small hop, and the ball rolls on into the bowl (new: candy bar)
  (() => {
    const s1 = biscuit(150, 230, 240, 22, 8);
    return {
      id: 'clack', par: 1, tools: { jelly_candy: 1 }, learn: ['materials', 'ramp'], intro: 'jelly_candy',
      statics: [s1, chocolate(900, 760, 600, 80)],
      balls: [on(s1, -80, P)],
      goal: { type: 'bowl', x: 780, y: 720, w: 260 },
      objs: [{ t: 'duck', x: 560, y: 600 }],
      solution: [{ t: 'jelly_candy', x: 400, y: 500, a: 15 }],
    };
  })(),

  // 4 – High hop – which material? Only the soft jelly sends the ball high enough for the bowl (candy and marshmallow are decoys)
  (() => {
    const s1 = biscuit(190, 250, 300, 22, 6);
    return {
      id: 'high-hop', par: 1, tools: { jelly_block: 1, jelly_candy: 1, jelly_marshmallow: 1 }, learn: ['materials', 'spring'],
      statics: [s1, chocolate(330, 760, 660, 80), chocolate(900, 540, 480, 40), chocolate(660, 650, 30, 260)],
      balls: [on(s1, -120, P)],
      goal: { type: 'bowl', x: 900, y: 520, w: 220 },
      objs: [{ t: 'duck', x: 480, y: 620 }],
      solution: [{ t: 'jelly_block', x: 480, y: 560, a: 15 }],
      fails: [[{ t: 'jelly_candy', x: 480, y: 560, a: 15 }], [{ t: 'jelly_marshmallow', x: 480, y: 560, a: 15 }], [{ t: 'jelly_candy', x: 480, y: 620, a: 30 }]],
    };
  })(),

  // 5 – Heavy ball – the jawbreaker rolls down and pushes the gumball over the gap; candyfloss is too light (new: jawbreaker; candyfloss is a decoy here)
  (() => {
    const s1 = biscuit(330, 220, 420, 22, 25);
    return {
      id: 'heavy-ball', par: 1, tools: { jelly_heavy: 1, jelly_light: 1 }, learn: ['weight', 'energy'], intro: 'jelly_heavy',
      statics: [s1, biscuit(600, 332, 170, 22), chocolate(330, 760, 660, 80), chocolate(1000, 680, 400, 240)],
      balls: [{ x: 640, y: 304, r: P }],
      goal: { type: 'bowl', x: 980, y: 560, w: 240 },
      objs: [{ t: 'duck', x: 760, y: 200 }],
      solution: [{ t: 'jelly_heavy', x: 200, y: 120 }],
      fails: [[{ t: 'jelly_light', x: 200, y: 120 }]],
    };
  })(),

  // 6 – Soft landing – the jelly wall sends the ball back; a marshmallow on the wall stops it above the bowl (extra jelly is a decoy)
  (() => {
    const s1 = biscuit(200, 230, 300, 22, 30);
    return {
      id: 'soft-landing', par: 1, tools: { jelly_marshmallow: 1, jelly_block: 1 }, learn: ['materials', 'energy'],
      statics: [s1, biscuit(450, 318, 240, 22), chocolate(260, 760, 520, 80), chocolate(900, 790, 600, 20)],
      fixed: [{ t: 'jelly_block', x: 800, y: 440, a: 90 }],
      balls: [on(s1, -110, P)],
      goal: { type: 'bowl', x: 720, y: 780, w: 150 },
      objs: [{ t: 'duck', x: 560, y: 200 }],
      solution: [{ t: 'jelly_marshmallow', x: 750, y: 440, a: 0 }],
      fails: [[{ t: 'jelly_block', x: 766, y: 440, a: 90 }]],
    };
  })(),

  // 7 – Angle – the ball leaves the jelly at the same angle it hits it; turn the jelly so it hits the jar
  {
    id: 'angle', par: 1, tools: { jelly_block: 1 }, learn: ['direction', 'spring'],
    statics: [biscuit(240, 150, 140, 22), chocolate(240, 310, 30, 300), chocolate(380, 310, 30, 300), chocolate(820, 520, 300, 30), chocolate(600, 780, 1200, 40)],
    balls: [{ x: 310, y: 120, r: P, vx: 0.5 }],
    goal: { type: 'bell', x: 820, y: 460 },
    objs: [{ t: 'duck', x: 540, y: 300 }],
    solution: [{ t: 'jelly_block', x: 310, y: 700, a: 15 }],
    fails: [[{ t: 'jelly_block', x: 310, y: 720, a: 0 }], [{ t: 'jelly_block', x: 310, y: 720, a: -30 }]],
  },

  // 8 – Gentle push – the ball only needs a nudge over the edge; the jawbreaker pushes it too far (candyfloss intro)
  (() => {
    const s1 = biscuit(300, 220, 440, 22, 25);
    return {
      id: 'light-push', par: 1, tools: { jelly_heavy: 1, jelly_light: 1 }, learn: ['weight', 'energy'], intro: 'jelly_light',
      statics: [s1, biscuit(640, 330, 260, 22), chocolate(500, 780, 1000, 40)],
      balls: [{ x: 700, y: 303, r: P }],
      goal: { type: 'bowl', x: 830, y: 760, w: 150 },
      objs: [{ t: 'duck', x: 1000, y: 500 }],
      solution: [{ t: 'jelly_light', x: 200, y: 140 }],
      fails: [[{ t: 'jelly_heavy', x: 200, y: 140 }]],
    };
  })(),

  // 9 – Double hop – the gap is too wide for one hop; two jelly blocks as stepping stones (candy is a decoy)
  (() => {
    const s1 = biscuit(160, 330, 300, 22, 14);
    return {
      id: 'double-hop', par: 2, tools: { jelly_block: 2, jelly_candy: 1 }, learn: ['spring', 'energy'],
      statics: [s1, chocolate(140, 600, 280, 360), chocolate(1060, 720, 280, 160)],
      balls: [on(s1, -120, P)],
      goal: { type: 'bowl', x: 1060, y: 640, w: 220 },
      objs: [{ t: 'duck', x: 640, y: 420 }],
      solution: [{ t: 'jelly_block', x: 540, y: 680, a: 0 }, { t: 'jelly_block', x: 850, y: 680, a: 0 }],
      fails: [[{ t: 'jelly_block', x: 540, y: 680, a: 0 }], [{ t: 'jelly_block', x: 540, y: 680, a: 0 }, { t: 'jelly_candy', x: 850, y: 680, a: 0 }]],
    };
  })(),

  // 10 – Little hop – the candy gives a small clack hop over the edge; the jelly sends the ball far too far
  {
    id: 'small-hop', par: 1, tools: { jelly_candy: 1, jelly_block: 1 }, learn: ['materials', 'energy'],
    statics: [biscuit(330, 120, 90, 22), chocolate(300, 690, 300, 180), chocolate(480, 660, 60, 240), chocolate(700, 780, 600, 40)],
    balls: [{ x: 330, y: 160, r: P }],
    goal: { type: 'bowl', x: 620, y: 760, w: 160 },
    objs: [{ t: 'duck', x: 1000, y: 400 }],
    solution: [{ t: 'jelly_candy', x: 330, y: 585, a: 15 }],
    fails: [[{ t: 'jelly_block', x: 330, y: 585, a: 15 }]],
  },

  // 11 – Heavy against heavy – candyfloss barely moves the heavy jawbreaker ball; another jawbreaker pushes it over the gap
  (() => {
    const s1 = biscuit(330, 220, 420, 22, 25);
    return {
      id: 'heavy-vs-heavy', par: 1, tools: { jelly_light: 1, jelly_heavy: 1 }, learn: ['weight', 'energy'],
      statics: [s1, biscuit(600, 332, 170, 22), chocolate(330, 760, 660, 80), chocolate(975, 680, 450, 240)],
      balls: [{ x: 640, y: 302, r: 18, skin: 'jelly_jaw', opts: { density: 0.012, restitution: 0.5 } }],
      goal: { type: 'bowl', x: 880, y: 560, w: 220 },
      objs: [{ t: 'duck', x: 500, y: 520 }],
      solution: [{ t: 'jelly_heavy', x: 200, y: 120 }],
      fails: [[{ t: 'jelly_light', x: 200, y: 120 }], [{ t: 'jelly_light', x: 480, y: 250 }]],
    };
  })(),

  // 12 – Push and hop – the jawbreaker pushes the ball out, the jelly sends it up into the jar (two steps)
  (() => {
    const s1 = biscuit(330, 220, 420, 22, 25);
    return {
      id: 'push-and-hop', par: 2, tools: { jelly_heavy: 1, jelly_light: 1, jelly_block: 1, jelly_candy: 1 }, learn: ['weight', 'spring', 'energy'],
      statics: [s1, biscuit(600, 332, 170, 22), chocolate(330, 760, 660, 80), chocolate(1080, 460, 200, 30)],
      balls: [{ x: 640, y: 304, r: P }],
      goal: { type: 'bell', x: 1080, y: 400 },
      objs: [{ t: 'duck', x: 860, y: 200 }],
      solution: [{ t: 'jelly_heavy', x: 200, y: 120 }, { t: 'jelly_block', x: 840, y: 540, a: 0 }],
      fails: [[{ t: 'jelly_block', x: 840, y: 540, a: 0 }], [{ t: 'jelly_heavy', x: 200, y: 120 }, { t: 'jelly_candy', x: 840, y: 540, a: 0 }], [{ t: 'jelly_light', x: 200, y: 120 }, { t: 'jelly_block', x: 840, y: 540, a: 0 }]],
    };
  })(),

  // 13 – Clack and stop – the candy hops the ball onto the ski jump, the marshmallow stops it at the bowl
  {
    id: 'clack-and-stop', par: 2, tools: { jelly_candy: 1, jelly_marshmallow: 1, jelly_block: 1 }, learn: ['materials', 'energy'],
    statics: [biscuit(200, 120, 90, 22), chocolate(200, 690, 360, 180), biscuit(527, 641, 292, 22, 12), biscuit(718, 672, 100, 22), chocolate(960, 780, 480, 40)],
    balls: [{ x: 200, y: 160, r: P }],
    goal: { type: 'bowl', x: 830, y: 760, w: 150 },
    objs: [{ t: 'duck', x: 560, y: 400 }],
    solution: [{ t: 'jelly_candy', x: 200, y: 585, a: 30 }, { t: 'jelly_marshmallow', x: 900, y: 650, a: 0 }],
    fails: [[{ t: 'jelly_candy', x: 200, y: 585, a: 30 }], [{ t: 'jelly_marshmallow', x: 900, y: 650, a: 0 }]],
  },

  // 14 – Push, hop and stop – combine: the jawbreaker pushes, the jelly hops, the marshmallow stops the ball at the jelly wall
  (() => {
    const s1 = biscuit(330, 220, 420, 22, 25);
    return {
      id: 'push-hop-stop', par: 3, tools: { jelly_heavy: 1, jelly_light: 1, jelly_block: 1, jelly_marshmallow: 1, jelly_candy: 1 }, learn: ['weight', 'spring', 'materials'],
      statics: [s1, biscuit(600, 332, 170, 22), chocolate(330, 760, 660, 80), chocolate(950, 780, 500, 40)],
      fixed: [{ t: 'jelly_block', x: 1080, y: 380, a: 90 }],
      balls: [{ x: 640, y: 304, r: P }],
      goal: { type: 'bowl', x: 1010, y: 760, w: 150 },
      objs: [{ t: 'duck', x: 860, y: 200 }],
      solution: [{ t: 'jelly_heavy', x: 200, y: 120 }, { t: 'jelly_block', x: 840, y: 540, a: 0 }, { t: 'jelly_marshmallow', x: 990, y: 370, a: 0 }],
      fails: [[{ t: 'jelly_heavy', x: 200, y: 120 }, { t: 'jelly_block', x: 840, y: 540, a: 0 }],
        [{ t: 'jelly_heavy', x: 200, y: 120 }, { t: 'jelly_candy', x: 840, y: 540, a: 0 }, { t: 'jelly_marshmallow', x: 990, y: 370, a: 0 }],
        [{ t: 'jelly_light', x: 200, y: 120 }, { t: 'jelly_block', x: 840, y: 540, a: 0 }, { t: 'jelly_marshmallow', x: 990, y: 370, a: 0 }]],
    };
  })(),

  // 15 – Master – mirrored: the jawbreaker pushes, the jelly hops the ball over the chocolate tower, the marshmallow stops it at the jelly wall
  (() => {
    const s1 = biscuit(870, 220, 420, 22, -25);
    return {
      id: 'master', par: 3, tools: { jelly_heavy: 1, jelly_light: 1, jelly_block: 1, jelly_candy: 1, jelly_marshmallow: 1 }, learn: ['weight', 'spring', 'materials', 'energy'],
      statics: [s1, biscuit(600, 332, 170, 22), chocolate(870, 760, 660, 80), chocolate(250, 780, 500, 40), chocolate(250, 640, 40, 240)],
      fixed: [{ t: 'jelly_block', x: 120, y: 380, a: 90 }],
      balls: [{ x: 560, y: 304, r: P }],
      goal: { type: 'bowl', x: 190, y: 760, w: 150 },
      objs: [{ t: 'duck', x: 340, y: 200 }],
      solution: [{ t: 'jelly_heavy', x: 1000, y: 120 }, { t: 'jelly_block', x: 360, y: 540, a: 0 }, { t: 'jelly_marshmallow', x: 210, y: 370, a: 0 }],
      fails: [[{ t: 'jelly_light', x: 1000, y: 120 }, { t: 'jelly_block', x: 360, y: 540, a: 0 }, { t: 'jelly_marshmallow', x: 210, y: 370, a: 0 }],
        [{ t: 'jelly_heavy', x: 1000, y: 120 }, { t: 'jelly_candy', x: 360, y: 540, a: 0 }, { t: 'jelly_marshmallow', x: 210, y: 370, a: 0 }],
        [{ t: 'jelly_heavy', x: 1000, y: 120 }, { t: 'jelly_block', x: 360, y: 540, a: 0 }]],
    };
  })(),
]);
